import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { IBetaCalibration } from '@models/beta/beta-estimate';
import { ICalibrationChange, ICalibrationChangelog } from '@models/calibrations/calibration-changelog';
import { IDecayCalibration } from '@models/decay/decay-calibration';
import { IDecayCalibrationType, DecayCalibrationType } from '@models/decay/decay-calibration-type';
import { IFunctionalForm } from '@models/functional-form';
import { CalibrationStatus } from '@models/record-status';
import { ForecastMethods } from '@models/stable-balance/forecast-settings';
import { IStableBalanceCalibration } from '@models/stable-balance/stable-balance-calibration';
import { IStoreState } from '@models/store/store-state';
import { DecayCalibrationService } from '@services/decay/decay-calibration.service';
import { FunctionalFormService } from '@services/global/functional-form.service';
import { ObservableStore } from '@services/observable-store/dist';
import { getDateString } from '@shared/dates';
import { getForecastMethod } from '@shared/forecast-methods';
import { round } from '@shared/numbers';
import { parseDecayEstimateSetting } from '@shared/parse-decay-estimate-settings';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DecayCalibrationTypeService } from '@services/decay/decay-calibration-type.service';

@Injectable({ providedIn: 'root' })
export class ChangelogService extends ObservableStore<IStoreState> {
  private DECAY_CALIBRATIONS_FOR_PRODUCTS: string;
  private STABLE_BALANCE_CALIBRATIONS_FOR_PRODUCTS: string;
  private BETA_CALIBRATIONS_FOR_PRODUCT: string;

  private readonly functionalForms: IFunctionalForm[];
  private decayCalibrationTypes: IDecayCalibrationType[];
  private readonly propNames = {
    // Stable Balance
    CalibrationEndDate: 'Calibration End',
    CalibrationStartDate: 'Calibration Start',
    Damping: 'Damping',
    ForecastDownDamper: 'Forecast Down Damper',
    ForecastEndDate: 'Forecast End Date',
    ForecastGrowth: 'Forecast Growth',
    ForecastMethod: 'Forecast Method',
    ForecastStartDate: 'Forecast Start',
    ForecastUpDamper: 'Forecast Up Damper',
    Haircut: 'Haircut',
    HistoricalDownDamper: 'Historical Down Damper',
    HistoricalUpDamper: 'Historical Up Damper',
    LastObservationDate: 'Last Observation',
    SelectedPolynomialForm: 'Selected Polynomial Form',
    StdMultiplier: 'Standard Deviation Multiplier',
    StdTerm: 'Standard Deviation Term',

    // Decay
    Curtailment: 'Curtailment',
    EndObservation: 'End Observation',
    ExcludeAcquisitionIds: 'Excluded Acquisitions',
    FunctionalForm: 'Model',
    StartObservation: 'Start Observation',
    StartVintage: 'Start Vintage',
    DecayCalibrationType: 'Calibration Type',

    // Beta
    BetaCoefficient: 'Beta Coefficient',
    Lag: 'Lag',
    MarketRateId: 'Market Rate'
  };

  constructor(
    private readonly http: HttpClient,
    private readonly appConfig: AppConfigProvider,
    private readonly functionalFormService: FunctionalFormService,
    private readonly decayCalibrationTypeService: DecayCalibrationTypeService
  ) {
    super({ trackStateHistory: false, logStateChanges: false });

    this.appConfig.environment.subscribe(env => {
      this.DECAY_CALIBRATIONS_FOR_PRODUCTS = `${env.API.DECAY}/api/decay/calibration/forproduct`;
      this.STABLE_BALANCE_CALIBRATIONS_FOR_PRODUCTS = `${env.API.STABLE_BALANCE}/api/stablebalance/calibrationforproduct`;
      this.BETA_CALIBRATIONS_FOR_PRODUCT = `${env.API.BETA}/api/beta/calibration/forproduct`;
    });
    this.functionalForms = this.functionalFormService.getFunctionalFormsForDecay();
    this.decayCalibrationTypeService.getDecayCalibrationTypes().subscribe(types => (this.decayCalibrationTypes = types));
  }

  private getUserName(userId: number): string {
    const { users } = this.getState();
    if (users) {
      const user = users.find(u => u.UserId === userId);
      return user ? `${user.FirstName} ${user.LastName}` : null;
    }
    return null;
  }

  ////////////////////
  // Stable Balance //
  ///////////////////

  public getChangelogForStableBalanceCalibrationForProduct(
    productId: number
  ): Observable<ICalibrationChangelog<IStableBalanceCalibration>[]> {
    return this.getChangelogForCalibrationForProduct<IStableBalanceCalibration>(
      productId,
      this.STABLE_BALANCE_CALIBRATIONS_FOR_PRODUCTS
    ).pipe(map(changeLogs => this.parseStableBalanceChangeLogs(changeLogs)));
  }

  public parseStableBalanceChangeLogs(changeLogs: ICalibrationChangelog<IStableBalanceCalibration>[]) {
    if (changeLogs) {
      changeLogs.forEach((changelog, index) => {
        // Summary
        changelog.Summary = this.getStableBalanceChangeLogSummary(changeLogs, index);

        // Calibration
        // @ts-ignore
        changelog.Calibration.Settings.Forecast.ForecastMethod = ForecastMethods[changelog.Calibration.Settings.Forecast.ForecastMethod];
        if (typeof changelog.Calibration.Coefficients === 'string') {
          try {
            changelog.Calibration.Coefficients = JSON.parse(changelog.Calibration.Coefficients);
          } catch (error) {
            changelog.Calibration.Coefficients = [];
          }
        }

        if (typeof changelog.Calibration.SelectedAcquisitionIds === 'string') {
          // If is string separated by commas, conver to array of numbers
          changelog.Calibration.SelectedAcquisitionIds = `${changelog.Calibration.SelectedAcquisitionIds as string}`
            .split(',')
            .map(elem => parseInt(elem, 10));
        }

        changelog.Calibration.SelectedAcquisitionIds = [...new Set(changelog.Calibration.SelectedAcquisitionIds)];
      });

      return changeLogs;
    }
  }

  public getChangelogForCalibrationForProduct<T>(productId: number, apiUrl: string): Observable<ICalibrationChangelog<T>[]> {
    return this.http
      .get<ICalibrationChangelog<T>[]>(`${apiUrl}/${productId}/changelog`)
      .pipe(map(changelogs => this.processChangeLogs<T>(changelogs)));
  }

  public processChangeLogs<T>(changelogs: ICalibrationChangelog<T>[]): ICalibrationChangelog<T>[] {
    changelogs.forEach(changelog => {
      changelog = this.transformChangelogCalibrationJSON(changelog);
      changelog.UserName = this.getUserName(changelog.UserId);
    });

    return changelogs.sort((a, b) => b.ChangelogId - a.ChangelogId);
  }

  private transformChangelogCalibrationJSON<T>(changelog: ICalibrationChangelog<T>): ICalibrationChangelog<T> {
    try {
      // Convert CalibrationJSON to each Calibration model
      const standarizedJsonStr = changelog.CalibrationJSON
        // RecordStatus
        .replace('record_status', 'RecordStatus')
        // FormId
        .replace('form_id', 'FormId');
      changelog.Calibration = JSON.parse(standarizedJsonStr) as T;
      delete changelog.CalibrationJSON;
    } catch (err) {
      // console.warn('ERROR building the CalibrationJSON', err);
    }
    return changelog;
  }

  private getStableBalanceChangeLogSummary(
    changelogs: ICalibrationChangelog<IStableBalanceCalibration>[],
    index: number
  ): ICalibrationChange[] {
    const changelog = changelogs[index];
    let changedProps: ICalibrationChange[] = [];

    if (index === changelogs.length - 1) {
      changedProps.push({ property: 'Calibration created' });
    } else {
      const changelogOriginal = changelogs[index + 1];
      changedProps = this.getStableBalanceChangelogDiff(changelogOriginal.Calibration, changelog.Calibration);
    }
    return changedProps;
  }

  public getStableBalanceChangelogDiff(
    calibrationPrevious: IStableBalanceCalibration,
    calibrationCurrent: IStableBalanceCalibration
  ): ICalibrationChange[] {
    const changedProps: ICalibrationChange[] = [];

    // Record Status Changed
    if (
      (!calibrationCurrent.ChangeLogs || calibrationCurrent.ChangeLogs.length === 0) &&
      (!calibrationPrevious.ChangeLogs || calibrationPrevious.ChangeLogs.length === 0) &&
      calibrationPrevious.RecordStatus !== calibrationCurrent.RecordStatus &&
      calibrationPrevious.RecordStatus !== CalibrationStatus.Rejected
    ) {
      if (calibrationCurrent.FormId === 'Recalculation') {
        changedProps.push({ property: 'Recalculation' });
      } else {
        let msg = '';
        switch (calibrationCurrent.RecordStatus) {
          case CalibrationStatus.Active:
            msg = 'Calibration Approved';
            break;
          case CalibrationStatus.Inactive:
            msg = 'Automatically Deactivated';
            break;
          case CalibrationStatus.Rejected:
            msg = 'Calibration Rejected';
            break;
          case CalibrationStatus.Proposed:
            msg = 'Calibration Proposed';
            break;

          default:
            break;
        }
        changedProps.push({ property: msg });
        return changedProps;
      }
    }

    const calibrationProperties = [
      'CalibrationEndDate',
      'CalibrationStartDate',
      'ForecastEndDate',
      'ForecastStartDate',
      'LastObservationDate',
      'SelectedPolynomialForm'
    ];
    const forecastSettings = ['ForecastDownDamper', 'ForecastGrowth', 'ForecastMethod', 'ForecastUpDamper'];
    const historicalSettings = ['Damping', 'Haircut', 'HistoricalDownDamper', 'HistoricalUpDamper', 'StdMultiplier', 'StdTerm'];

    // Main Properties
    calibrationProperties.forEach(prop => {
      const different = prop.includes('Date')
        ? getDateString(calibrationCurrent[prop]) !== getDateString(calibrationPrevious[prop])
        : calibrationCurrent[prop] !== calibrationPrevious[prop];

      if (different) {
        changedProps.push({
          property: prop,
          previous: calibrationPrevious[prop],
          current: calibrationCurrent[prop]
        });
      }
    });

    // Settings
    if (calibrationCurrent.Settings && calibrationPrevious.Settings) {
      forecastSettings.forEach(prop => {
        if (calibrationCurrent.Settings.Forecast[prop] !== calibrationPrevious.Settings.Forecast[prop]) {
          changedProps.push({
            property: prop,
            previous: prop === 'ForecastMethod' ? getForecastMethod(calibrationPrevious) : calibrationPrevious.Settings.Forecast[prop],
            current: prop === 'ForecastMethod' ? getForecastMethod(calibrationCurrent) : calibrationCurrent.Settings.Forecast[prop]
          });
        }
      });

      historicalSettings.forEach(prop => {
        if (calibrationCurrent.Settings.Historical[prop] !== calibrationPrevious.Settings.Historical[prop]) {
          if (['HistoricalDownDamper', 'HistoricalUpDamper'].includes(prop)) {
            changedProps.push({
              property: prop,
              previous: calibrationPrevious.Settings.Historical[prop] * 100,
              current: calibrationCurrent.Settings.Historical[prop] * 100
            });
          } else {
            changedProps.push({
              property: prop,
              previous: calibrationPrevious.Settings.Historical[prop],
              current: calibrationCurrent.Settings.Historical[prop]
            });
          }
        }
      });
    }

    // Selected Acquisitions
    if (JSON.stringify(calibrationCurrent.SelectedAcquisitionIds) !== JSON.stringify(calibrationPrevious.SelectedAcquisitionIds)) {
      changedProps.push({ property: 'Selected Acquisitions' });
    }

    return changedProps;
  }

  public getPropName(prop: string): string {
    return (this.propNames && this.propNames[prop]) || prop;
  }

  ////////////
  // Decay //
  //////////

  private normalizeExcludeAcquisitions(calibration: IDecayCalibration): IDecayCalibration {
    // Normalization of ExcludeAcquisitions to ExcludeAcquisitionIds
    if (calibration && calibration.hasOwnProperty('ExcludeAcquisitions')) {
      calibration.ExcludeAcquisitionIds =
        // tslint:disable-next-line: no-string-literal
        calibration['ExcludeAcquisitions']
          // convert to number[]
          .split(',')
          .map(n => +n) as number[];
      // delete calibration['ExcludeAcquisitions'];
      // @ts-ignore
      delete calibration.ExcludeAcquisitions;
    }

    return calibration;
  }

  public getChangelogForDecayCalibrationForProduct(productId: number): Observable<ICalibrationChangelog<IDecayCalibration>[]> {
    return this.getChangelogForCalibrationForProduct<IDecayCalibration>(productId, this.DECAY_CALIBRATIONS_FOR_PRODUCTS).pipe(
      map(changelogs => this.parseDecayChangeLogs(changelogs))
    );
  }

  public parseDecayChangeLogs(changeLogs: ICalibrationChangelog<IDecayCalibration>[]) {
    if (changeLogs) {
      changeLogs.forEach((changelog, index) => {
        changelog.Calibration = this.normalizeExcludeAcquisitions(changelog.Calibration);
        changelog.Calibration = parseDecayEstimateSetting(changelog.Calibration);

        // Summary needs to be built after DecayEstimateSetting parsing
        changelog.Summary = this.getDecayChangelogSummary(changeLogs, index);
      });

      return changeLogs;
    }
  }

  private getDecayChangelogSummary(changelogs: ICalibrationChangelog<IDecayCalibration>[], index: number): ICalibrationChange[] {
    const changelog = changelogs[index];
    let changedProps: ICalibrationChange[] = [];

    if (index === changelogs.length - 1) {
      changedProps.push({ property: 'Calibration created' });
    } else {
      const changelogOriginal = changelogs[index + 1];
      changedProps = this.getDecayChangelogDiff(changelogOriginal.Calibration, changelog.Calibration);
    }
    return changedProps;
  }

  public getDecayChangelogDiff(calibrationOriginal: IDecayCalibration, calibrationNew: IDecayCalibration): ICalibrationChange[] {
    const changedProps: ICalibrationChange[] = [];

    const chosenEstimateOriginal = (parseDecayEstimateSetting(calibrationOriginal).DecayEstimateSetting || [])[0];
    const chosenEstimateNew = (parseDecayEstimateSetting(calibrationNew).DecayEstimateSetting || [])[0];
    if (
      (!calibrationNew.ChangeLogs || calibrationNew.ChangeLogs.length === 0) &&
      (!calibrationOriginal.ChangeLogs || calibrationOriginal.ChangeLogs.length === 0) &&
      calibrationOriginal.RecordStatus !== calibrationNew.RecordStatus &&
      calibrationOriginal.RecordStatus !== CalibrationStatus.Rejected
    ) {
      let msg = '';
      switch (calibrationNew.RecordStatus) {
        case CalibrationStatus.Active:
          msg = 'Calibration Approved';
          break;
        case CalibrationStatus.Inactive:
          msg = 'Automatically Deactivated';
          break;
        case CalibrationStatus.Rejected:
          msg = 'Calibration Rejected';
          break;
        case CalibrationStatus.Proposed:
          msg = 'Calibration Proposed';
          break;

        default:
          break;
      }
      changedProps.push({ property: msg });
    } else {
      const calibrationProperties = ['ExcludeAcquisitionIds', 'StartDate', 'DecayCalibrationType', 'ImpliedCurtailment'];
      const chosenEstimateProperties = ['Curtailment', 'EndObservation', 'FunctionalForm', 'StartObservation'];
      let oldCurtailment;
      let newCurtailment;

      // Checking for changes in the Decay calibration
      calibrationProperties.forEach(property => {
        let previous = calibrationOriginal[property];
        let current = calibrationNew[property];
        let different = false;

        switch (property) {
          case 'DecayCalibrationType':
            const previousType = this && this.decayCalibrationTypes && this.decayCalibrationTypes.find(f => f.Id === previous);
            const currentType = this && this.decayCalibrationTypes && this.decayCalibrationTypes.find(f => f.Id === current);
            if (previous !== current) {
              different = true;
              previous = previousType ? previousType.Name : previous;
              current = currentType ? currentType.Name : current;
            }
            break;
          case 'StartDate':
            const propertyFallback = 'StartVintage';
            if (!previous) {
              previous = calibrationOriginal[propertyFallback] || '';
            }
            if (!current) {
              current = calibrationNew[propertyFallback] || '';
            }
            if (previous.substr(0, 7) !== current.substr(0, 7)) {
              different = true;
            }
            break;
          case 'ExcludeAcquisitionIds':
            if (JSON.stringify(previous || []) !== JSON.stringify(current || [])) {
              different = true;
              previous = null;
              current = null;
            }
            break;
          case 'ImpliedCurtailment':
            const propName = 'ImpliedCurtailment';
            if (calibrationNew.hasOwnProperty(propName)) {
              newCurtailment = calibrationNew[propName];
            }

            if (calibrationOriginal.hasOwnProperty(propName)) {
              oldCurtailment = calibrationOriginal[propName];
            }
            break;
          default:
            break;
        }
        if (different) {
          changedProps.push({ property, previous, current });
        }
      });

      if (chosenEstimateOriginal && chosenEstimateNew) {
        // Checking for changes in the chosen estimate
        chosenEstimateProperties.forEach(property => {
          let previous = chosenEstimateOriginal[property];
          let current = chosenEstimateNew[property];

          switch (property) {
            case 'FunctionalForm':
              if (previous !== current) {
                const previousFunctionalForm = this && this.functionalForms && this.functionalForms.find(f => f.Index === previous);
                const currentFunctionalForm = this && this.functionalForms && this.functionalForms.find(f => f.Index === current);
                changedProps.push({
                  property,
                  previous: previousFunctionalForm ? previousFunctionalForm.Name : previous,
                  current: currentFunctionalForm ? currentFunctionalForm.Name : current
                });
              }
              break;
            case 'Curtailment':
              if (calibrationNew.DecayCalibrationType === DecayCalibrationType.AvgClosedAccounts) {
                current = newCurtailment || (calibrationNew.DecayFactors ? calibrationNew.DecayFactors.length : null);
                previous = oldCurtailment || (calibrationOriginal.DecayFactors ? calibrationOriginal.DecayFactors.length : null);
              } else {
                newCurtailment = current;
                oldCurtailment = previous;
              }
              break;
            case 'StartObservation':
            case 'EndObservation':
              if (previous !== current) {
                changedProps.push({
                  property,
                  previous,
                  current
                });
              }
              break;
            default:
              break;
          }
        });
      }
      if (newCurtailment !== oldCurtailment) {
        changedProps.push({
          property: 'Curtailment',
          previous: oldCurtailment,
          current: newCurtailment
        });
      }
    }

    return changedProps;
  }

  //////////
  // Beta //
  /////////

  public getBetaChangeLogsForProduct(productId: number): Observable<ICalibrationChangelog<IBetaCalibration>[]> {
    return this.getChangelogForCalibrationForProduct<IBetaCalibration>(productId, this.BETA_CALIBRATIONS_FOR_PRODUCT).pipe(
      map(changeLogs => this.parseBetaChangeLogs(changeLogs))
    );
  }

  public parseBetaChangeLogs(changeLogs: ICalibrationChangelog<IBetaCalibration>[]) {
    if (changeLogs) {
      changeLogs.forEach((changelog, index) => {
        changelog.Summary = this.getBetaChangeLogSummary(changeLogs, index);
      });
      return changeLogs;
    }
  }

  public getBetaChangeLogSummary(changeLogs: ICalibrationChangelog<IBetaCalibration>[], index: number): ICalibrationChange[] {
    const changelog = changeLogs[index];
    let changedProps: ICalibrationChange[] = [];

    if (index === changeLogs.length - 1) {
      changedProps.push({
        property: 'Calibration created'
      });
    } else {
      const changelogOriginal = changeLogs[index + 1];
      changedProps = this.getBetaChangeLogDiff(changelogOriginal.Calibration, changelog.Calibration);
    }
    return changedProps;
  }

  public getBetaChangeLogDiff(calibrationOriginal: IBetaCalibration, calibrationNew: IBetaCalibration): ICalibrationChange[] {
    const changedProps: ICalibrationChange[] = [];

    if (calibrationOriginal && calibrationNew) {
      if (calibrationNew.FormId === 'Recalculation') {
        changedProps.push({ property: 'Recalculation' });
      } else {
        if (
          calibrationOriginal.RecordStatus !== calibrationNew.RecordStatus &&
          calibrationOriginal.RecordStatus !== CalibrationStatus.Rejected
        ) {
          let msg = '';
          switch (calibrationNew.RecordStatus) {
            case CalibrationStatus.Active:
              msg = 'Calibration Approved';
              break;
            case CalibrationStatus.Inactive:
              msg = 'Automatically Deactivated';
              break;
            case CalibrationStatus.Rejected:
              msg = 'Calibration Rejected';
              break;
            case CalibrationStatus.Proposed:
              msg = 'Calibration Proposed';
              break;
            default:
              break;
          }
          changedProps.push({ property: msg });
        } else {
          const calibrationProperties = ['BetaCoefficient', 'MarketRateId', 'Lag'];

          calibrationProperties.forEach(property => {
            if (calibrationOriginal[property] !== calibrationNew[property]) {
              // Beta Coefficient
              if (property === calibrationProperties[0]) {
                let decimals = 2;
                let previous = round(calibrationOriginal[property], decimals);
                let current = round(calibrationNew[property], decimals);
                while (previous === current && decimals <= 6) {
                  decimals++;
                  previous = round(calibrationOriginal[property], decimals);
                  current = round(calibrationNew[property], decimals);
                }
                changedProps.push({ property, previous, current });
              } else {
                changedProps.push({
                  property,
                  previous: calibrationOriginal[property],
                  current: calibrationNew[property]
                });
              }
            }
          });
        }
      }
    }
    return changedProps;
  }
}
